import React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  VStack,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Button,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { ThemedFormLabel } from "../../../homeowners/hire/themed-form-label";
import { ThemedInput } from "../../../homeowners/hire/themed-input";
import { 
  JobConsultationFormData,
  jobConsultationSchema 
} from "../../schemas/recruitment-consultation.schema";
import {
  EMPLOYMENT_TYPE_OPTIONS,
  DURATION_TYPE_OPTIONS,
} from "../../types/recruitment-consultation.types";
import { ThemedSelect } from "../../../homeowners/hire/themed-select";
import { useRecruitmentConsultation } from "../../context/recruitment-consultation.context";

interface JobConsultationStepProps {
  onStepComplete: (data: JobConsultationFormData) => void;
  onPrevious: () => void;
  isLoading?: boolean;
}

export const JobConsultationStep: React.FC<JobConsultationStepProps> = ({
  onStepComplete,
  onPrevious,
  isLoading = false,
}) => {
  const { getJobConsultation } = useRecruitmentConsultation();
  const existingData = getJobConsultation();
  
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<JobConsultationFormData>({
    resolver: zodResolver(jobConsultationSchema),
    defaultValues: {
      ...existingData,
      duration: existingData.duration || { type: "MONTHS" as any, value: 1 },
    },
  });

  const onSubmit = (data: JobConsultationFormData) => {
    onStepComplete(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={6} align="stretch">
        <FormControl isInvalid={!!errors.jobTitle}>
          <ThemedFormLabel>Job Title</ThemedFormLabel>
          <ThemedInput
            {...register("jobTitle")}
            placeholder="e.g., Plumbing Technician"
          />
          <FormErrorMessage>{errors.jobTitle?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.salaryRange}>
          <ThemedFormLabel>Salary Range</ThemedFormLabel>
          <ThemedInput
            {...register("salaryRange")}
            placeholder="e.g., N250,000 - N350,000"
          />
          <FormErrorMessage>{errors.salaryRange?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.employmentType}>
          <ThemedFormLabel>Employment Type</ThemedFormLabel>
          <ThemedSelect
            {...register("employmentType")}
            placeholder="Select employment type"
            size="lg"
          >
            {EMPLOYMENT_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </ThemedSelect>
          <FormErrorMessage>{errors.employmentType?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.duration}>
          <ThemedFormLabel>Contract Duration</ThemedFormLabel>
          <HStack>
            <Controller
              name="duration.value"
              control={control}
              render={({ field }) => (
                <NumberInput
                  {...field}
                  value={field.value || ""}
                  onChange={(valueString) => {
                    field.onChange(Number(valueString));
                  }}
                  min={1}
                  size="lg"
                  variant="outline"
                  flex="1"
                  borderColor="gray.400"
                >
                  <NumberInputField rounded="none" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              )}
            />
            <ThemedSelect {...register("duration.type")} size="lg" flex="1">
              {DURATION_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </ThemedSelect>
          </HStack>
          <FormErrorMessage>{errors.duration?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.location}>
          <ThemedFormLabel>Location</ThemedFormLabel>
          <ThemedInput
            {...register("location")}
            placeholder="e.g., Lagos, Nigeria"
          />
          <FormErrorMessage>{errors.location?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.expectedStartDate}>
          <ThemedFormLabel>Expected Start Date</ThemedFormLabel>
          <Controller
            name="expectedStartDate"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                type="date"
                size="lg"
                variant="outline"
                rounded="none"
                borderColor="gray.400"
                value={
                  field.value ? field.value.toISOString().split("T")[0] : ""
                }
                onChange={(e) => field.onChange(new Date(e.target.value))}
              />
            )}
          />
          <FormErrorMessage>
            {errors.expectedStartDate?.message}
          </FormErrorMessage>
        </FormControl>

        <HStack spacing={4} mt={4}>
          <Button
            type="button"
            onClick={onPrevious}
            leftIcon={<ArrowBackIcon />}
            size="lg"
            flex="1"
            variant="outline"
            isDisabled={isLoading}
          >
            Previous
          </Button>
          <Button
            type="submit"
            size="lg"
            flex="1"
            colorScheme="flatteredFlamingo"
            isLoading={isLoading}
            loadingText="Submitting..."
          >
            Submit Request
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};
